import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from "xlsx-js-style";

import React, { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Button,
  Table,
  Card,
  CardHeader,
  CardBody,
  Label,
  Input,
  Spinner,
  Modal,
  ModalHeader,
  ModalFooter,
  ModalBody,
} from 'reactstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createTimeSheet,
  CreateTimeSheetDTO,
  deleteTimeSheet,
  getAllTimeSheets,
  getUserProfile,
  TimeSheetResponseDTO,
  updateTimeSheet,
  getProjectNames,
  getAllUsers,
  getNotFilledTimeSheets,
  getProjects,
  getHolidaysByYear,
} from '../../Api/ApiUrlConst';
import { useAuth } from '../../Context/AuthContext';
import { toast } from 'react-toastify';
import { HourglassBottom } from '@mui/icons-material';
import { ProjectDTO } from '../../types/client.types';

interface User {
  userID: string;
  name: string;
  email: string;
}
interface ProjectNameDto {
  projectID: number;
  projectName: string;
}
const STATIC_MONTH_LIST = (() => {
  const list: string[] = [];

  const startYear = 2025;
  const startMonth = 9; // September (1-based)

  const now = new Date();
  const endYear = now.getFullYear();
  const endMonth = now.getMonth() + 1; // current month (1-based)

  for (let y = startYear; y <= endYear; y++) {
    const fromMonth = y === startYear ? startMonth : 1;
    const toMonth = y === endYear ? endMonth : 12;

    for (let m = fromMonth; m <= toMonth; m++) {
      list.push(`${y}-${String(m).padStart(2, "0")}`);
    }
  }

  // Latest month first
  return list.sort((a, b) => b.localeCompare(a));
})();

const CombinedTimesheetComponent: React.FC = () => {
  const { name, role } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    projectName: '',
    userName: 'All',
  });

  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editableData, setEditableData] = useState<Partial<CreateTimeSheetDTO> | null>(null);
  const [newRowData, setNewRowData] = useState<Partial<CreateTimeSheetDTO>>({});
  const [modal, setModal] = useState(false);
  const [selectedTimeId, setSelectedTimeId] = useState<string | null>(null);


  const [notFilledModal, setNotFilledModal] = useState(false);
  const [notFilledData, setNotFilledData] = useState<any[]>([]);
  const [notFilledLoading, setNotFilledLoading] = useState(false);
  const [notFilledNameFilter, setNotFilledNameFilter] = useState("");
  const [partModalOpen, setPartModalOpen] = useState(false);
  const [partModalText, setPartModalText] = useState("");
  const [descrptionOpen, setdescrptionOpen] = useState(false);
  const [descrptionText, setdescrptionText] = useState("");

  const [selectedMonth, setSelectedMonth] = useState("");
  const [exportModal, setExportModal] = useState(false);
  const toggleExportModal = () => setExportModal(!exportModal);
  const [holidays, setHolidays] = useState<string[]>([]);
  const [showCompLeave, setShowCompLeave] = useState(false);
  const [taskFilter, setTaskFilter] = useState("");
  const [taskTypeFilter, setTaskTypeFilter] = useState("");
  const [drawingTypeFilter, setDrawingTypeFilter] = useState("");


  const [clientexportModal, setClientexportModal] = useState(false);
  const toggleclientExportModal = () => setClientexportModal(!clientexportModal);


  const toggle = () => setModal(!modal);
  const queryClient = useQueryClient();


  const { data: projects = [] } = useQuery<ProjectDTO[]>({
    queryKey: ["projects"],
    queryFn: getProjects,
  });

  const projectIdMap: Record<string, string> = {};

  projects.forEach(p => {
    projectIdMap[p.projectName] = p.projectID;
  });



  // ✅ Utility: current date
  const getCurrentDate = (): string => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // ✅ Fetch users
  // ✅ Fetch users (Admin only)
  const fetchUsers = async () => {
    try {
      const res = await getAllUsers();
      setUsers(res.data?.$values || res.data || []);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  };

  useEffect(() => {
    if (!filters.startDate) return;

    const year = new Date(filters.startDate).getFullYear();

    getHolidaysByYear(year).then(res => {
      const dates = res.data.map(h => h.date.split("T")[0]);
      setHolidays(dates);
    });
  }, [filters.startDate]);

  const isHoliday = (date: string) => {
    return holidays.includes(date.split("T")[0]);
  };


  const isTimesheetFilled = (userName: string, date: string) => {
    return timesheets.some(
      t =>
        t.userName?.toLowerCase() === userName?.toLowerCase() &&
        t.date.split("T")[0] === date.split("T")[0]
    );
  };

  // ✅ Fetch timesheets
  // const { data: timesheets = [], isLoading } = useQuery({
  //   queryKey: ['timesheets', filters],
  //   queryFn: () =>
  //     getAllTimeSheets(filters.startDate, filters.endDate, filters.projectName || undefined),
  // });

  const { data: allTimesheets = [], isLoading } = useQuery({
    queryKey: ['timesheets', filters],
    queryFn: () =>
      getAllTimeSheets(filters.startDate, filters.endDate, filters.projectName, filters.userName || undefined),
  });

  const isWeekend = (value?: string | Date) => {
    if (!value) return false;

    let date: Date;

    if (typeof value === "string") {
      const [y, m, d] = value.split("T")[0].split("-").map(Number);
      date = new Date(y, m - 1, d);
    } else {
      date = value;
    }

    const day = date.getDay();
    return day === 0 || day === 6;
  };

  // 🔹 Filter user data (only show their own if not admin/pm/tl)
  const timesheets = React.useMemo(() => {
    let data = [...allTimesheets];

    if (filters.startDate && filters.endDate) {
      const start = new Date(filters.startDate + "T00:00:00");
      const end = new Date(filters.endDate + "T23:59:59");

      data = data.filter(t => {
        const d = new Date(t.date);
        return d >= start && d <= end;
      });
    }

    // 🔥 Compensation Leave Filter
    if (showCompLeave) {
      data = data.filter(t => {
        const date = t.date;
        return (
          (isHoliday(date) || isWeekend(date)) &&
          t.hours > 0 // only filled
        );
      });
    }

    // Normal role filter
    if (role !== "Admin") {
      data = data.filter(sheet =>
        sheet.userName?.toLowerCase() === name?.toLowerCase()
      );
    }

    return data;
  }, [allTimesheets, filters, role, name, showCompLeave]);

  // ✅ Fetch user profile
  const { data: userProfile } = useQuery({
    queryKey: ['userProfile'],
    queryFn: () => getUserProfile().then((res) => res.data),
  });

  // ✅ Fetch project names
  const { data: projectNames = [] } = useQuery<string[]>({
    queryKey: ['projectNames'],
    queryFn: getProjectNames,
  });

  const isAdmin = userProfile?.role === 'Admin';
  const worksheet = role === 'Admin';

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);
  // ✅ Mutations

  const mutation = useMutation({
    mutationFn: (dto: CreateTimeSheetDTO | { timeId: string; dto: CreateTimeSheetDTO }) => {
      if ('timeId' in dto) return updateTimeSheet(dto.timeId, dto.dto).then((res) => res.data);
      return createTimeSheet(dto as CreateTimeSheetDTO).then((res) => res.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
      setEditingRow(null);
      setEditableData(null);
      setNewRowData({});
      toast.success('Timesheet saved successfully!');
    },
    onError: (error: any) => {
      console.error('Full error object:', error);

      const responseData = error?.response?.data;
      console.log('Backend error response:', responseData);

      const message =
        responseData?.message ||
        responseData?.error ||
        error?.message ||
        'Something went wrong. Please try again.';

      toast.error(message);
    },


  });

  const deleteMutation = useMutation({
    mutationFn: (timeId: string) => deleteTimeSheet(timeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
      toggle();
    },
  });

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const startEdit = (sheet: TimeSheetResponseDTO) => {
    setEditingRow(sheet.timeId);
    setEditableData({ ...sheet });
  };

  const handleCellChange = (field: keyof CreateTimeSheetDTO, value: string | number | null) => {
    setEditableData((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  const saveRow = (rowId: string) => {
    const dataToSave = rowId === 'new' ? newRowData : editableData;
    if (!dataToSave) return;

    const dto: CreateTimeSheetDTO = {
      ...dataToSave,
      // date: getCurrentDate(),
    } as CreateTimeSheetDTO;

    if (!dto.projectName || !dto.hours || !dto.assignedBy) {
      alert('Please fill all required fields.');
      return;
    }

    rowId === 'new' ? mutation.mutate(dto) : mutation.mutate({ timeId: rowId, dto });
  };

  const handleDelete = (id: string) => {
    setSelectedTimeId(id);
    toggle();
  };

  // ===================== TYPES =====================
  type UserHours = {
    [user: string]: number;
  };

  type TaskEntry = {
    project: string;
    task: string;
    users: UserHours;
    total: number;
    userDepartment: string;
  };

  type TaskMap = {
    [key: string]: TaskEntry;
  };

  type NumberMap = {
    [key: string]: number;
  };

  type Timesheet = {
    userName: string;
    projectName: string;
    module?: string;
    hours: number;
    userDepartment?: string;
    date: string;
  };

  //   // 🔹 Put this first
  // const applyLast7Days = () => {
  //   const end = new Date();
  //   const start = new Date();
  //   start.setDate(end.getDate() - 7);

  //   setFilters(prev => ({
  //     ...prev,
  //     startDate: start.toISOString().split("T")[0],
  //     endDate: end.toISOString().split("T")[0],
  //     projectName: "",
  //     userName: "All"
  //   }));

  //   setSelectedMonth("");
  // };

  // // 🔹 THEN useEffect
  // useEffect(() => {
  //   applyLast7Days();
  // }, []);

  // 🔹 Put this ABOVE useEffect
  const applyLast7Days = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 7);

    setFilters(prev => ({
      ...prev,
      startDate: start.toISOString().split("T")[0],
      endDate: end.toISOString().split("T")[0],
      projectName: "",
      userName: "All"
    }));

    setSelectedMonth("");
  };

  // 🔹 Keep ONLY THIS useEffect
  useEffect(() => {
    applyLast7Days();
  }, []);


  const createStyledSheet = (rows: any[]) => {

    const ws = XLSX.utils.json_to_sheet(rows, {
      skipHeader: false,
    });

    const range = XLSX.utils.decode_range(ws["!ref"] || "");

    // ======================================================
    // HEADER STYLE
    // ======================================================

    for (let col = range.s.c; col <= range.e.c; col++) {

      const cellAddress = XLSX.utils.encode_cell({
        r: 0,
        c: col,
      });

      if (!ws[cellAddress]) continue;

      ws[cellAddress].s = {
        font: {
          bold: true,
          color: { rgb: "000000" },
        },

        fill: {
          fgColor: { rgb: "FFFF00" },
        },

        alignment: {
          horizontal: "center",
          vertical: "center",
        },

        border: {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" },
        },
      };
    }

    // ======================================================
    // DATA STYLE
    // ======================================================

    for (let row = 1; row <= range.e.r; row++) {

      for (let col = range.s.c; col <= range.e.c; col++) {

        const cellAddress = XLSX.utils.encode_cell({
          r: row,
          c: col,
        });

        if (!ws[cellAddress]) continue;

        const cell = ws[cellAddress];

        const value = cell.v;

        // ✅ Detect Number or "-"
        const isNumberOrDash =
          typeof value === "number" || value === "-";

        // ✅ Fix cell type
        cell.t =
          typeof value === "number"
            ? "n"
            : "s";

        // ✅ Apply style
        cell.s = {
          border: {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
          },

          alignment: {
            horizontal: isNumberOrDash
              ? "center"
              : "left",

            vertical: "center",
          },
        };
      }
    }

    // ======================================================
    // COLUMN WIDTH
    // ======================================================

    ws["!cols"] = Object.keys(rows[0] || {}).map((key) => ({
      wch:
        key === "Department" ? 18 :
          key === "Project Name" ? 25 :
            key === "Task Detail" ? 30 :
              key === "Total Hours" ? 15 :
                15,
    }));

    return ws;
  };



  // ===================== EXPORT FUNCTION =====================
  const buildRows = (data: Timesheet[]) => {

    const allUsers: string[] = Array.from(
      new Set(data.map((t) => t.userName))
    ).sort((a, b) => a.localeCompare(b));

    const taskMap: TaskMap = {};
    const projectTotals: NumberMap = {};
    const userTotals: NumberMap = {};

    allUsers.forEach((u) => (userTotals[u] = 0));

    data.forEach((t) => {

      const taskName = t.module || "-";

      const key = `${t.projectName}|${taskName}`;

      const hrs = Number(t.hours || 0);

      if (!taskMap[key]) {
        taskMap[key] = {
          project: t.projectName,
          task: taskName,
          users: {},
          total: 0,
          userDepartment: t.userDepartment || "-",
        };
      }

      if (!taskMap[key].users[t.userName]) {
        taskMap[key].users[t.userName] = 0;
      }

      taskMap[key].users[t.userName] += hrs;

      taskMap[key].total += hrs;

      userTotals[t.userName] += hrs;

      if (!projectTotals[t.projectName]) {
        projectTotals[t.projectName] = 0;
      }

      projectTotals[t.projectName] += hrs;
    });

    const rows: any[] = (Object.values(taskMap) as TaskEntry[])
      .sort((a, b) => {

        const p = a.project.localeCompare(b.project);

        if (p !== 0) return p;

        return a.task.localeCompare(b.task);

      })
      .map((row) => {

        const obj: any = {
          Department: row.userDepartment,
          "Project ID": projectIdMap[row.project] || "",
          "Project Name": row.project,
          "Task Detail": row.task,
        };

        // ✅ USER HOURS AS NUMBER
        allUsers.forEach((user) => {

          obj[user] =
            row.users[user] !== undefined
              ? Number(row.users[user])
              : "-";

        });

        // ✅ TOTAL HOURS AS NUMBER
        obj["Total Hours"] = Number(row.total || 0);

        return obj;
      });

    // ======================================================
    // TOTAL ROW
    // ======================================================

    const totalRow: any = {
      "Task Detail": "Total Hours",
    };

    let grandTotal = 0;

    allUsers.forEach((user) => {

      totalRow[user] = Number(userTotals[user] || 0);

      grandTotal += Number(userTotals[user] || 0);

    });

    totalRow["Total Hours"] = Number(grandTotal || 0);

    rows.push(totalRow);

    return rows;
  };




  // ===================== EXPORT FUNCTION =====================


  const buildTotalSheet = (data: Timesheet[]) => {

    const TEAM_LEADERS = ["Santhosh S"]
      .map(n => n.toLowerCase());

    const projectMap: any = {};
    const mechUsers: any = {};
    const elecUsers: any = {};

    let mechTotal = 0;
    let elecTotal = 0;
    let tlTotal = 0;
    let memTotal = 0;

    data.forEach(t => {
      const projNo = t.projectName.split(" ")[0] || "-";
      const projName = t.projectName;
      const hrs = Number(t.hours || 0);
      const dept = t.userDepartment?.toLowerCase() || "";
      const user = (t.userName || "").toLowerCase();

      if (!projectMap[projName]) {
        projectMap[projName] = {
          projectNo: projNo,
          projectName: projName,
          mech: 0,
          elec: 0,
          tl: 0,
          mem: 0,
        };
      }

      // department totals
      if (dept === "mechanical") {
        projectMap[projName].mech += hrs;
        mechTotal += hrs;
        mechUsers[t.userName] =
          (mechUsers[t.userName] || 0) + hrs;
      }

      if (dept === "electrical") {
        projectMap[projName].elec += hrs;
        elecTotal += hrs;
        elecUsers[t.userName] =
          (elecUsers[t.userName] || 0) + hrs;
      }

      // TL vs Member totals
      if (TEAM_LEADERS.includes(user)) {
        projectMap[projName].tl += hrs;
        tlTotal += hrs;
      } else {
        projectMap[projName].mem += hrs;
        memTotal += hrs;
      }
    });

    const rows: any[] = [];

    // ----- Project Summary -----
    Object.values(projectMap)
      .sort((a: any, b: any) =>
        a.projectName.localeCompare(b.projectName)
      )
      .forEach((p: any) => {
        rows.push({
          ProjectNo: projectIdMap[p.projectName] || "",
          ProjectName: p.projectName,
          TotalHours: p.mech + p.elec,
          MechanicalHours: p.mech,
          ElectricalHours: p.elec,
          TeamLeadHours: p.tl,
          TeamMembersHours: p.mem,
        });
      });

    rows.push({
      ProjectName: "Total Hours",
      TotalHours: mechTotal + elecTotal,
      MechanicalHours: mechTotal,
      ElectricalHours: elecTotal,
      TeamLeadHours: tlTotal,
      TeamMembersHours: memTotal,
    });

    rows.push({});
    rows.push({});

    // ----- Mechanical Users -----
    rows.push({ ProjectNo: "Mechanical Users", ProjectName: "Total Hours" });

    Object.entries(mechUsers)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .forEach(([u, h]) => {
        rows.push({ ProjectNo: u, ProjectName: h });
      });

    rows.push({ ProjectNo: "Total Hours", ProjectName: mechTotal });

    rows.push({});
    rows.push({});

    // ----- Electrical Users -----
    rows.push({ ProjectNo: "Electrical Users", ProjectName: "Total Hours" });

    Object.entries(elecUsers)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .forEach(([u, h]) => {
        rows.push({ ProjectNo: u, ProjectName: h });
      });

    rows.push({ ProjectNo: "Total Hours", ProjectName: elecTotal });

    return rows;
  };






  const handleExport = () => {

    if (!timesheets.length) {
      alert("No data to export.");
      return;
    }

    const mechanical = timesheets.filter(
      t => t.userDepartment?.toLowerCase() === "mechanical"
    );

    const electrical = timesheets.filter(
      t => t.userDepartment?.toLowerCase() === "electrical"
    );

    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      createStyledSheet(buildRows(mechanical)),
      "Mechanical"
    );

    XLSX.utils.book_append_sheet(
      wb,
      createStyledSheet(buildRows(electrical)),
      "Electrical"
    );

    XLSX.utils.book_append_sheet(
      wb,
      createStyledSheet(buildTotalSheet(timesheets)),
      "Total Hours"
    );

    const monthYear = new Date(filters.startDate).toLocaleString("en-US", {
      month: "short",
      year: "numeric",
    });

    const fileName = `Timesheet ${monthYear}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };


  const clienthandleExport = () => {

    if (!timesheets.length) {
      alert("No data to export.");
      return;
    }

    const mechanical = timesheets.filter(
      t => t.userDepartment?.toLowerCase() === "mechanical"
    );

    const electrical = timesheets.filter(
      t => t.userDepartment?.toLowerCase() === "electrical"
    );

    const wb = XLSX.utils.book_new();

    // ✅ Total Hours (Custom Layout Sheet)
    XLSX.utils.book_append_sheet(
      wb,
      createTotalHoursSheet(timesheets), // 🔥 pass raw data
      "Total Hours"
    );

    // ✅ Mechanical Sheet (Normal Table)
    XLSX.utils.book_append_sheet(
      wb,
      createStyledSheet(buildRows(mechanical)),
      "Mechanical"
    );

    // ✅ Electrical Sheet (Normal Table)
    XLSX.utils.book_append_sheet(
      wb,
      createStyledSheet(buildRows(electrical)),
      "Electrical"
    );

    const monthYear = new Date(filters.startDate).toLocaleString("en-US", {
      month: "short",
      year: "numeric",
    });

    const fileName = `Timesheet ${monthYear}.xlsx`;

    XLSX.writeFile(wb, fileName);
  };









  const hourOptions = [0.25, 0.5, 0.75, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12];


  const prefixOptions = [
    "Modelling",
    "Modelling Update",
    "Modelling Review",
    "Drafting",
    "Drafting Update",
    "Drafting Review",
    "CP Request",
    "Release",
    "Training",
    "Teamcentre",
    "Client Meeting",
    "Management",
    "ISO",
    "Others",


  ];
  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      setSelectedMonth(filters.startDate.slice(0, 7));
    } else {
      setSelectedMonth("");
    }
  }, [filters.startDate, filters.endDate]);


  const drawingTypeOptions = [
    { code: "6MM", label: "6MM", full: "Standard 6 mm thickness part drawing" },

    { code: "2SG", label: "2SG", full: "2 Subgroup assembly drawing " },

    { code: "3EA", label: "3EA", full: "3 Element assembly (Extended) " },

    { code: "4MA", label: "4MA", full: "4 Member assembly drawing " },

    { code: "8PP", label: "8PP", full: "8 Part package drawing " },

    { code: "COMP-WELD", label: "COMP-WELD", full: "Complicated weldments drawing " },

    { code: "1FA", label: "1FA" },

    { code: "Others", label: "Other" },
  ];


  const assignedByOptions = ['ElaiyaPerumal', 'Santhosh S', 'Santhosh R', 'Renganathan', 'Umesh', 'Vishal', 'Sabari', 'Sengamuthu'];

  if (isLoading)
    return (
      <div className="text-center p-4">
        <Spinner color="primary" style={{ height: '3rem', width: '3rem' }} type="grow">
          Loading...
        </Spinner>
      </div>
    );

  const excludeEmails = [
    "renganathan@wocons.com",
    "santhoshs@wocons.com",
    "santhoshr@wocons.com",
    "ravindrababu@wocons.com",
    "projectmanager@wocons.com",
    "hr@wocons.com",
    "itmanager@gmail.com",
    "gokul@feesworth.com",
    "muthupandu400@gmail.com",
    "krish@gmail.com",
    "Perumal@wocons.com",
    "limya.shilin1@donaldson.com",
    "vishnu@wocons.com",
    "client@wocons.com",
    "Rizalde.Barrato@Donaldson.com",
    "Vishnu.Prasath@Donaldson.com",
    "limya@wocons.com",
    "krishsengamuthu@gmail.com",
    "gokulkannan199805@gmail.com",
    "abc@xy.com",
    "aishwarya.jahagirdar@wocons.com",
    "priyadharshini.k@wocons.com",
    "blessi.sam@wocons.com"
  ]

  const formatDateWithDay = (date: Date = new Date()) => {
    const day = date.toLocaleDateString("en-US", { day: "2-digit" });
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const year = date.getFullYear();

    return ` ${day}-${month}-${year}`;
  };

  const getDayName = (date: Date = new Date()) =>
    date.toLocaleDateString("en-US", { weekday: "short" })
      .toUpperCase();

  const formatDate2 = (value?: string | Date) => {
    if (!value) return "—";

    const date = typeof value === "string" ? new Date(value) : value;

    const day = date.toLocaleDateString("en-US", { day: "2-digit" });
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const getDayNamenew = (value?: string | Date) => {
    if (!value) return "—";
    const date = typeof value === "string" ? new Date(value) : value;
    return date.toLocaleDateString("en-US", { weekday: "short" })
      .toUpperCase();
  };

  const isToday = (value?: string | Date) => {
    if (!value) return false;

    let date: Date;

    if (typeof value === "string") {
      const [y, m, d] = value.split("T")[0].split("-").map(Number);
      date = new Date(y, m - 1, d); // local date (fix timezone issue)
    } else {
      date = value;
    }

    const today = new Date();

    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };


  const isSameDay = (d1: Date, d2: Date) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  const getDayName2 = (value?: string | Date) => {
    if (!value) return "—";

    let date: Date;

    if (typeof value === "string") {
      const [y, m, d] = value.split("T")[0].split("-").map(Number);
      date = new Date(y, m - 1, d);
    } else {
      date = value;
    }

    // 👉 Hide today's date
    if (isSameDay(date, new Date())) {
      return "—";
    }

    return date
      .toLocaleDateString("en-US", { weekday: "short" })
      .toUpperCase();
  };

  const loadNotFilledTimesheets = async () => {
    if (!filters.startDate || !filters.endDate) {
      toast.warning("Please select Start Date and End Date");
      return;
    }

    setNotFilledLoading(true);
    setNotFilledData([]); // 🔒 clear old data

    try {
      const res = await getNotFilledTimeSheets(
        filters.startDate,
        filters.endDate,
        filters.projectName || undefined,
        filters.userName !== "All" ? filters.userName : undefined
      );

      setNotFilledData(res.data.records || []);
      setNotFilledModal(true); // ✅ modal only
    } catch (err) {
      toast.error("Failed to load not filled timesheets");
    } finally {
      setNotFilledLoading(false);
    }
  };

  const handleExportExcel = () => {
    if (!timesheets.length) return alert('No data to export.');
    const exportData = timesheets.map((t) => ({
      'User Name': t.userName,
      Department: t.userDepartment,
      Date: t.date,
      'Project Name': t.projectName,
      'Module / Part': t.module || '',
      'Sub Task': t.subTask || '',
      'Child Task': t.childSubTask || '',
      'Part No': t.part || '',
      Task: t.product || '',
      Description: t.description || '',
      Hours: t.hours,
      'Assigned By': t.assignedBy,
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Worksheet');
    XLSX.writeFile(wb, `WorkSheet_${getCurrentDate()}.xlsx`);
  };


  const clientbuildTotalSheet = (data: Timesheet[]) => {

    const projectMap: any = {};

    let mechTotal = 0;
    let elecTotal = 0;

    data.forEach(t => {
      const projName = t.projectName;
      const projNo = projectIdMap[projName] || "";
      const hrs = Number(t.hours || 0);
      const dept = t.userDepartment?.toLowerCase() || "";

      if (!projectMap[projName]) {
        projectMap[projName] = {
          ProjectNo: projNo,
          ProjectName: projName,
          MechanicalHours: 0,
          ElectricalHours: 0,
        };
      }

      if (dept === "mechanical") {
        projectMap[projName].MechanicalHours += hrs;
        mechTotal += hrs;
      }

      if (dept === "electrical") {
        projectMap[projName].ElectricalHours += hrs;
        elecTotal += hrs;
      }
    });

    const rows: any[] = [];

    // ✅ Project Rows
    Object.values(projectMap)
      .sort((a: any, b: any) =>
        a.ProjectName.localeCompare(b.ProjectName)
      )
      .forEach((p: any) => {
        rows.push({
          ProjectNo: p.ProjectNo,
          ProjectName: p.ProjectName,
          MechanicalHours: p.MechanicalHours,
          ElectricalHours: p.ElectricalHours,
          TotalHours: p.MechanicalHours + p.ElectricalHours,
        });
      });

    // ✅ Total Row (IMPORTANT FORMAT)
    rows.push({
      ProjectNo: "",
      ProjectName: "Total Hours",
      MechanicalHours: mechTotal,
      ElectricalHours: elecTotal,
      TotalHours: mechTotal + elecTotal,
    });

    return rows;
  };



  const createTotalHoursSheet = (data: Timesheet[]) => {
    const rows = clientbuildTotalSheet(data);
    const ws: any = {};

    let rowIndex = 0;

    // ===================== DATE =====================
    const date = new Date(filters.startDate);
    const year = date.getFullYear();
    const monthName = date.toLocaleString("en-US", { month: "long" });

    const startDate = new Date(year, date.getMonth(), 1);
    const endDate = new Date(year, date.getMonth() + 1, 0);

    const formatDate = (d: Date) =>
      d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });

    const billingPeriod = `${formatDate(startDate)} to ${formatDate(endDate)}`;

    // ===================== COMMON STYLES =====================
    const border = {
      top: { style: "thin" },
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
    };

    // ===================== TITLE =====================
    ws["A1"] = {
      v: "TIME SHEET",
      s: {
        font: { bold: true, sz: 16, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "305496" } }, // dark blue
        alignment: { horizontal: "center" },
      },
    };

    ws["A2"] = {
      v: "SUB-CONTRACTOR DETAILS",
      s: {
        font: { bold: true, color: { rgb: "000000" } },
        fill: { fgColor: { rgb: "C6EFCE" } },
        alignment: { horizontal: "center" },
      },
    };

    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
    ];

    rowIndex = 3;

    // ===================== INFO =====================
    //   const border = {
    //   top: { style: "thin" },
    //   bottom: { style: "thin" },
    //   left: { style: "thin" },
    //   right: { style: "thin" },
    // };

    const labelStyle = {
      font: { bold: true },
      fill: { fgColor: { rgb: "C6EFCE" } },

    };

    const valueStyle = {
      fill: { fgColor: { rgb: "C6EFCE" } }, // same color = connected look

    };

    // ===== ROW 1 =====
    ws[`A${rowIndex}`] = { v: "Year", s: labelStyle };
    ws[`B${rowIndex}`] = { v: year, s: valueStyle };

    ws[`C${rowIndex}`] = { v: "Name", s: labelStyle };
    ws[`D${rowIndex}`] = {
      v: "WCS - Bengaluru",
      s: valueStyle,
    };
    ws[`E${rowIndex}`] = { v: "", s: labelStyle };

    rowIndex++; // ✅ correct increment

    // ===== ROW 2 =====
    ws[`A${rowIndex}`] = { v: "Month", s: labelStyle };
    ws[`B${rowIndex}`] = { v: monthName, s: valueStyle };

    ws[`C${rowIndex}`] = { v: "Billing Period", s: labelStyle };
    ws[`D${rowIndex}`] = { v: billingPeriod, s: valueStyle };
    ws[`E${rowIndex}`] = { v: "", s: valueStyle };

    rowIndex++;

    // ===== ROW 3 =====
    ws[`A${rowIndex}`] = { v: "", s: labelStyle };
    ws[`B${rowIndex}`] = { v: "", s: labelStyle };
    ws[`C${rowIndex}`] = { v: "Billing Entity", s: labelStyle };
    ws[`D${rowIndex}`] = { v: "DONALDSON ABUDHABI (DMEF) - LARGE TURBINE", s: valueStyle };
    ws[`E${rowIndex}`] = { v: "", s: valueStyle };

    rowIndex++;
    // ===== ROW 4 =====
    ws[`A${rowIndex}`] = { v: "", s: labelStyle };
    ws[`B${rowIndex}`] = { v: "", s: labelStyle };
    ws[`C${rowIndex}`] = { v: "", s: labelStyle };
    ws[`D${rowIndex}`] = { v: "", s: valueStyle };
    ws[`E${rowIndex}`] = { v: "", s: valueStyle };

    // ===================== HEADER =====================
    const headers = [
      "ProjectNo",
      "ProjectName",
      "Mechanical Hours",
      "Electrical Hours",
      "Total Hours",
    ];

    headers.forEach((h, i) => {
      const cell = XLSX.utils.encode_cell({ r: rowIndex, c: i });

      ws[cell] = {
        v: h,
        s: {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "305496" } }, // dark blue
          alignment: { horizontal: "center" },
          border,
        },
      };
    });

    rowIndex++;

    // ===================== DATA =====================
    rows.forEach((r, idx) => {
      const isTotal = r.ProjectName === "Total Hours";

      const bgColor = isTotal
        ? "C6EFCE" // green total row
        : idx % 1 === 0
          ? "FFFFFF"
          : "F2F2F2"; // zebra

      const style = {
        fill: { fgColor: { rgb: bgColor } },
        border,
      };

      ws[XLSX.utils.encode_cell({ r: rowIndex, c: 0 })] = {
        v: r.ProjectNo,
        s: style,
      };

      ws[XLSX.utils.encode_cell({ r: rowIndex, c: 1 })] = {
        v: r.ProjectName,
        s: { ...style, font: isTotal ? { bold: true } : {} },
      };

      [r.MechanicalHours, r.ElectricalHours, r.TotalHours].forEach((val, i) => {
        ws[XLSX.utils.encode_cell({ r: rowIndex, c: i + 2 })] = {
          v: Number(val || 0),
          t: "n",
          s: {
            ...style,
            font: isTotal ? { bold: true } : {},
            alignment: { horizontal: "center" },
          },
        };
      });

      rowIndex++;
    });

    // ===================== FOOTER =====================
    rowIndex += 2;

    ws[`A${rowIndex}`] = {
      v: "Please find department wise task details in the respective sheet",
      s: { font: { bold: true, color: { rgb: "000000" } } },
    };

    ws["!merges"].push({
      s: { r: rowIndex - 1, c: 0 },
      e: { r: rowIndex - 1, c: 5 },
    });

    // ===================== WIDTH =====================
    ws["!cols"] = [
      { wch: 15 },
      { wch: 28 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
    ];

    ws["!ref"] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: rowIndex, c: 4 },
    });

    return ws;
  };

  const excludeUsers = [
    "renganathan@wocons.com",
    "santhoshs@wocons.com",
    "santhoshr@wocons.com",
    "ravindrababu@wocons.com",
    "projectmanager@wocons.com",
    "hr@wocons.com",
    "itmanager@gmail.com",
    "krishsengamuthu@gmail.com",
    "Perumal@wocons.com",
    "projectmanager@wocons.com",
    "hr@wocons.com",
    "gokul@feesworth.com",
    "muthupandu400@gmail.com",
    "vishnu@wocons.com",
    "krish@gmail.com",
    "gokulkannan199805@gmail.com",
    "client@wocons.com",
    "limya@wocons.com",
    "Rizalde.Barrato@Donaldson.com",
    "Vishnu.Prasath@Donaldson.com",
    "limya.shilin1@donaldson.com",
    "abc@xy.com",
    "aishwarya.jahagirdar@wocons.com",
    "priyadharshini.k@wocons.com",
    "blessi.sam@wocons.com"
  ];

  const monthHours: Record<string, number> = {};

  timesheets.forEach(t => {
    const d = new Date(t.date);
    const monthKey = d.toLocaleString("en-US", { month: "short" });

    if (!monthHours[monthKey]) monthHours[monthKey] = 0;

    monthHours[monthKey] += Number(t.hours || 0);
  });

  const monthHoursText = Object.entries(monthHours)
    .map(([month, hrs]) => `${month}: ${hrs} hrs`)
    .join(" | ");




  const getHoursColor = (sheet: any) => {
    const totalHours = getDateTotalHours(sheet.date);

    if (totalHours < 12) return "#fff3cd"; // yellow warning

    return "";
  };

  const getDateTotalHours = (date: string) => {
    return timesheets
      .filter(t => t.date.split("T")[0] === date.split("T")[0])
      .reduce((sum, t) => sum + (t.hours ?? 0), 0);
  };


  const getRowColor = (rowData: any) => {
    if (isHoliday(rowData.date) && isTimesheetFilled(rowData.name, rowData.date)) {
      return "#fff3cd";
    }
    return "";
  };

  const userFirstFilledDate: Record<string, string> = {};

  timesheets.forEach(t => {
    const user = t.userName?.toLowerCase();
    const date = t.date.split("T")[0];

    if (!userFirstFilledDate[user]) {
      userFirstFilledDate[user] = date;
    } else {
      // keep earliest date
      if (date < userFirstFilledDate[user]) {
        userFirstFilledDate[user] = date;
      }
    }
  });


  //   const timesheets = React.useMemo(() => {
  //   let data = [...allTimesheets];

  //   if (filters.startDate && filters.endDate) {
  //     const start = new Date(filters.startDate + "T00:00:00");
  //     const end = new Date(filters.endDate + "T23:59:59");

  //     data = data.filter(t => {
  //       const d = new Date(t.date);
  //       return d >= start && d <= end;
  //     });
  //   }

  //   // 🔥 Compensation Leave Filter
  //   if (showCompLeave) {
  //     data = data.filter(t => {
  //       const date = t.date;
  //       return (
  //         (isHoliday(date) || isWeekend(date)) && 
  //         t.hours > 0 // only filled
  //       );
  //     });
  //   }

  //   // Normal role filter
  //   if (role !== "Admin") {
  //     data = data.filter(sheet =>
  //       sheet.userName?.toLowerCase() === name?.toLowerCase()
  //     );
  //   }

  //   return data;
  // }, [allTimesheets, filters, role, name, showCompLeave]);


  const filteredTimesheets =
    timesheets.filter((t) => {

      const taskMatch =
        !taskFilter ||
        t.module === taskFilter;

      const taskTypeMatch =
        !taskTypeFilter ||
        t.product === taskTypeFilter;

      const drawingTypeMatch =
        !drawingTypeFilter ||
        t.draftingType ===
        drawingTypeFilter;

      return (
        taskMatch &&
        taskTypeMatch &&
        drawingTypeMatch
      );
    });







  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <Card>

            <CardHeader className="d-flex justify-content-between align-items-center">

              <h5 className="m-0 fw-bold text-primary" style={{ width: "450px" }}>
                {worksheet ? 'WorkSheet Overall' : `WorkSheet - ${name}`}
                {monthHoursText && (
                  <span className="ms-2 text-success">
                    ({monthHoursText})
                  </span>
                )}
              </h5>

              {/* Show only TeamLead and TeamMembers */}
              {(role === "TeamLeader" || role === "TeamMember") && (
                <div className="d-flex gap-2">
                  <Col md={1}>
                  <Input
  type="select"
  name="projectName"
  value={filters.projectName}
  onChange={handleFilterChange}
>
  <option value="">All Projects</option>

  {projectNames
    .filter(
      (name) =>
        name !== "Priceworth" &&
        name !== "TaskManager" &&
        name !== "TaskManager Deploy"
    )
    .map((name) => (
      <option key={name} value={name}>
        {name}
      </option>
    ))}
</Input>
                  </Col>
                  <Col md={2}>

                    <Input
                      type="select"
                      value={taskFilter}
                      onChange={(e) =>
                        setTaskFilter(e.target.value)
                      }
                    >
                      <option value="">All</option>

                      {Array.from(
                        new Set(
                          timesheets
                            .map((t) => t.module)
                            .filter(Boolean)
                        )
                      ).map((task: any) => (
                        <option
                          key={task}
                          value={task}
                        >
                          {task}
                        </option>
                      ))}
                    </Input>
                  </Col>

                  <Col md={1}>

                    <Input
                      type="select"
                      value={taskTypeFilter}
                      onChange={(e) =>
                        setTaskTypeFilter(
                          e.target.value
                        )
                      }
                    >
                      <option value="">All</option>

                      {Array.from(
                        new Set(
                          timesheets
                            .map((t) => t.product)
                            .filter(Boolean)
                        )
                      ).map((type: any) => (
                        <option
                          key={type}
                          value={type}
                        >
                          {type}
                        </option>
                      ))}
                    </Input>
                  </Col>

                  <Col md={1}>

                    <Input
                      type="select"
                      value={drawingTypeFilter}
                      onChange={(e) =>
                        setDrawingTypeFilter(
                          e.target.value
                        )
                      }
                    >
                      <option value="">All</option>

                      {Array.from(
                        new Set(
                          timesheets
                            .map((t) => t.draftingType)
                            .filter(Boolean)
                        )
                      ).map((type: any) => (
                        <option
                          key={type}
                          value={type}
                        >
                          {type}
                        </option>
                      ))}
                    </Input>
                  </Col>

                  <Button color="primary"
                    onClick={() => {
                      const end = new Date();
                      const start = new Date();
                      start.setDate(end.getDate() - 7);

                      setFilters({
                        ...filters,
                        startDate: start.toISOString().split("T")[0],
                        endDate: end.toISOString().split("T")[0],
                      });
                    }}
                  >
                    Last 7 Days
                  </Button>


                  <Button color="info"
                    onClick={() => {
                      const now = new Date();
                      const year = now.getFullYear();
                      const month = now.getMonth() + 1;

                      const start = `${year}-${String(month).padStart(2, '0')}-01`;
                      const lastDay = new Date(year, month, 0).getDate();
                      const end = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;

                      setFilters({ ...filters, startDate: start, endDate: end });
                    }}
                  >
                    This Month
                  </Button>

                  <Button
                    color="warning"
                    onClick={() => {
                      const now = new Date();

                      // Go to LAST MONTH safely (handles Jan → Dec)
                      const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);

                      const year = lastMonthDate.getFullYear();
                      const month = lastMonthDate.getMonth() + 1; // convert to 1-based

                      const start = `${year}-${String(month).padStart(2, '0')}-01`;

                      // Last day of last month
                      const lastDay = new Date(year, month, 0).getDate();
                      const end = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;

                      setFilters({
                        ...filters,
                        startDate: start,
                        endDate: end
                      });
                    }}
                  >
                    Last Month
                  </Button>

                  {/* MONTH DROPDOWN */}
                  <Input
                    type="select"
                    value={selectedMonth}
                    style={{ width: "150px" }}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedMonth(value);

                      if (!value) {
                        setFilters(prev => ({ ...prev, startDate: "", endDate: "" }));
                        queryClient.invalidateQueries({ queryKey: ['timesheets'] });
                        return;
                      }

                      const [year, month] = value.split("-");

                      // Normal month start & end
                      const start = `${year}-${month}-01`;
                      const lastDay = new Date(Number(year), Number(month), 0).getDate();
                      const end = `${year}-${month}-${String(lastDay).padStart(2, "0")}`;

                      setFilters(prev => ({
                        ...prev,
                        startDate: start,
                        endDate: end
                      }));

                      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
                    }}
                  >
                    <option value="">Select Month</option>

                    {STATIC_MONTH_LIST.map(val => {
                      const [y, m] = val.split("-");
                      const monthName = new Date(Number(y), Number(m) - 1)
                        .toLocaleString("en-US", { month: "short" });

                      return (
                        <option key={val} value={val}>
                          {monthName} {y}
                        </option>
                      );
                    })}
                  </Input>

                  {/* ➕ CLEAR BUTTON */}
                  <Button
                    color="secondary"
                    onClick={() => {
                      setFilters({ startDate: "", endDate: "", projectName: "", userName: "All" });
                      setSelectedMonth("");
                    }}

                  >
                    Clear
                  </Button>

                </div>
              )}

              {isAdmin && (
                <div className='d-flex'>
                  <Button
                    color={showCompLeave ? "primary" : "secondary"}
                    onClick={() => setShowCompLeave(prev => !prev)}
                    style={{ marginRight: "-80px" }}
                  >
                    {showCompLeave ? "Compensation History" : "Compensation History"}
                  </Button>

                  <Button style={{ marginLeft: "100px" }} color="success" onClick={handleExportExcel}>
                    <DownloadIcon className="me-1" /> Download Excel
                  </Button>
                </div>
              )}

            </CardHeader>


            <CardBody>

              {isAdmin && (
                <Row className="mb-3">
                  <Col md={2}>
                    <Label className="fw-semibold">User</Label>
                    <Input
                      bsSize="sm"
                      type="select"
                      name="userName"
                      value={filters.userName}
                      onChange={handleFilterChange}
                    >
                      <option value="All">All</option>
                      {users.filter(u => !excludeEmails.includes(u.email)).sort((a, b) => a.name.localeCompare(b.name)).map((u) => (
                        <option key={u.userID} value={u.userID}>
                          {u.name}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={1}>
                    <Label>Start Date</Label>
                    <Input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} />
                  </Col>
                  <Col md={1}>
                    <Label>End Date</Label>
                    <Input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} />
                  </Col>
                  <Col md={2}>
                    <Label>Project Name</Label>
                    <Input type="select" name="projectName" value={filters.projectName} onChange={handleFilterChange}>
                      <option value="">All Projects</option>
                      {projectNames.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={3} className="d-flex align-items-end">
                    <Button color="info" onClick={() => queryClient.invalidateQueries({ queryKey: ['timesheets'] })}>
                      Apply Filters
                    </Button>

                    <Button
                      color="danger"
                      className="ms-2"
                      disabled={!filters.startDate || !filters.endDate}
                      onClick={loadNotFilledTimesheets}
                    >
                      Show Not Filled
                    </Button>
                    <Col md={3} >
                      <Button
                        style={{ width: "150px", marginLeft: "10px" }}
                        color="primary"
                        onClick={toggleExportModal}


                      >
                        <HourglassBottom className="me-1" /> TimeSheet
                      </Button>

                    </Col>

                    <Col md={1}>
                      <Button
                        color="primary"
                        style={{ width: "140px", marginLeft: "60px" }}
                        onClick={toggleclientExportModal}
                      // onClick={clienthandleExport}
                      >
                        Client Hours
                      </Button>
                    </Col>



                    <Col md={1}>
                      <Button color="secondary" onClick={applyLast7Days} style={{ marginLeft: "180px" }}>
                        Clear
                      </Button>

                    </Col>






                  </Col>



                </Row>


              )}
              <Modal
                isOpen={notFilledModal}
                toggle={() => setNotFilledModal(false)}
                size="lg"
              >
                <ModalHeader toggle={() => setNotFilledModal(false)}>
                  Not Filled Timesheets
                </ModalHeader>

                <ModalBody>
                  {notFilledLoading ? (
                    <div className="text-center">
                      <Spinner />
                    </div>
                  ) : notFilledData.length === 0 ? (
                    <p className="text-center text-success fw-bold">
                      ✅ All users have filled timesheets
                    </p>
                  ) : (
                    <>
                      {/* 🔍 Name Filter */}
                      <Input
                        type="text"
                        placeholder="Filter by user name..."
                        className="mb-3"
                        value={notFilledNameFilter}
                        onChange={(e) => setNotFilledNameFilter(e.target.value)}
                      />

                      <Table bordered hover responsive>
                        <thead className="table-secondary">
                          <tr>
                            <th>User Name</th>
                            <th>Date</th>
                            <th>Day</th>
                            <th>Status</th>
                          </tr>
                        </thead>


                        <tbody>
                          {(() => {

                            const filledUsers = new Set(
                              timesheets.map(t => t.userName?.toLowerCase())
                            );

                            const filtered = notFilledData.filter(row => {
                              const date = row.date.split("T")[0];
                              const user = row.name?.toLowerCase();

                              // ❌ Remove today
                              if (isToday(date)) return false;

                              // ❌ Remove weekend
                              if (isWeekend(date)) return false;

                              // ❌ Remove holiday
                              if (isHoliday(date)) return false;

                              // ❌ Exclude users
                              if (excludeUsers.includes(row.email)) return false;

                              // 🔥 IMPORTANT: Get first filled date
                              const firstDate = userFirstFilledDate[user];

                              // ❌ If user never filled anything → skip
                              if (!firstDate) return false;

                              // ❌ Remove dates BEFORE first filled date
                              if (date < firstDate) return false;

                              // 🔍 Name filter
                              if (notFilledNameFilter) {
                                return user.includes(notFilledNameFilter.toLowerCase());
                              }

                              return true;
                            });

                            if (filtered.length === 0) {
                              return (
                                <tr>
                                  <td colSpan={4} className="text-center text-warning fw-bold">
                                    No matching users found
                                  </td>
                                </tr>
                              );
                            }

                            return filtered.map((row, idx) => (

                              <tr key={idx}>
                                <td>{row.name}</td>
                                <td>{formatDate2(row.date)}</td>
                                <td>{getDayName2(row.date)}</td>
                                <td>
                                  {isHoliday(row.date) ? (
                                    isTimesheetFilled(row.name, row.date) ? (
                                      <span className="fw-bold text-warning">Holiday (Filled)</span>
                                    ) : (
                                      <span className="fw-bold text-primary">Holiday</span>
                                    )
                                  ) : (
                                    <span
                                      className={`fw-bold ${row.status === "Leave" ? "text-success" : "text-danger"
                                        }`}
                                    >
                                      {row.status || "Not Filled"}
                                    </span>
                                  )}
                                </td>
                              </tr>


                            ));
                          })()}
                        </tbody>

                      </Table>
                    </>
                  )}
                </ModalBody>


                <ModalFooter>
                  <Button color="secondary" onClick={() => setNotFilledModal(false)}>
                    Close
                  </Button>
                </ModalFooter>
              </Modal>

              {/* Table */}

              <div className="table-wrapper">
                <Table bordered hover responsive>

                  <thead className="table-primary">
                    <tr>
                      {worksheet && <th>User Name</th>}
                      <th >Date</th>
                      <th>Days</th>
                      <th >Hours</th>
                      <th>Project Name</th>
                      <th>Task</th>
                      <th style={{ width: "250px" }}>SubTask</th>
                      <th style={{ width: "130px" }}>ChildSubTask</th>
                      <th >Part No</th>
                      <th >Task Type</th>
                      <th style={{ width: "90px" }}>D.Type</th>
                      <th style={{ width: "80px" }}>D.Qty</th>
                      <th>Description / Remarks</th>
                      {isAdmin && <th></th>}
                    </tr>
                  </thead>

                  <tbody>
                    {/* <tr className="table-active">
                      {worksheet && (<> <td></td>
                        <td style={{ fontSize: "11px", fontWeight: "bold" }}>
                          {formatDateWithDay()}
                        </td>
                        <td style={{ fontSize: "11px", fontWeight: "bold" }}>
                          {getDayName()}
                        </td>
                        <td>
                          <Input
                            type="select"
                            value={newRowData.hours || ''}
                            onChange={(e) => setNewRowData({ ...newRowData, hours: Number(e.target.value) })}
                          >
                            <option value="">Hrs</option>
                            {hourOptions.map((h) => (
                              <option key={h} value={h}>
                                {h}
                              </option>
                            ))}
                          </Input>
                        </td>

                        <td>
                          <Input
                            type="select"
                            value={newRowData.projectName || ''}
                            onChange={(e) => setNewRowData({ ...newRowData, projectName: e.target.value })}
                          >
                            <option value="">Select Project</option>
                            {projectNames.map((p) => (
                              <option key={p} value={p}>
                                {p}
                              </option>
                            ))}
                          </Input>
                        </td>

                        <td>
                          <Input
                            type="select"
                            value={newRowData.module || ''}
                            onChange={(e) => setNewRowData({ ...newRowData, module: e.target.value })}
                          >
                            <option value="">Select Module</option>
                            {moduleOptions.map((m) => (
                              <option key={m} value={m}>
                                {m}
                              </option>
                            ))}
                          </Input>
                        </td>

                        <td>

                        </td>

                        <td>
                          <Input
                            type="text"
                            value={newRowData.part || ''}
                            onChange={(e) => setNewRowData({ ...newRowData, part: e.target.value })}
                          />
                        </td>

                        <td>
                          <Input
                            type="select"
                            value={newRowData.product || ''}
                            onChange={(e) => setNewRowData({ ...newRowData, product: e.target.value })}
                          >
                            <option value="">Select Task</option>
                            {prefixOptions.map((m) => (
                              <option key={m} value={m}>
                                {m}
                              </option>
                            ))}
                          </Input>
                        </td>

                        <td >
                          {newRowData.product === "Drafting" ? (
                            <Input
                              type="select"
                              value={newRowData.draftingType || ""}
                              onChange={(e) =>
                                setNewRowData({ ...newRowData, draftingType: e.target.value })
                              }
                            >
                              <option value="">Select Type</option>
                              {drawingTypeOptions.map((d) => (
                                <option key={d.code} value={d.code} title={d.full}>
                                  {d.label}
                                </option>
                              ))}
                            </Input>

                          ) : (
                            "—"
                          )}
                        </td>

                        <td>
                          {newRowData.product === "Drafting" ? (
                            <Input
                              type="number"
                              min={0}
                              value={newRowData.quantity ?? ""}
                              onChange={(e) =>
                                setNewRowData({
                                  ...newRowData,
                                  quantity: Number(e.target.value),
                                })
                              }
                            />
                          ) : (
                            "—"
                          )}
                        </td>

                        <td>
                          <Input
                            type="text"
                            value={newRowData.description || ''}
                            onChange={(e) => setNewRowData({ ...newRowData, description: e.target.value })}
                          />
                        </td>

                        <td className="d-flex">
                          <Input
                            type="select"
                            value={newRowData.assignedBy || ''}
                            onChange={(e) => setNewRowData({ ...newRowData, assignedBy: e.target.value })}
                            style={{ marginRight: '9px' }}
                          >
                            <option value="">Select</option>
                            {assignedByOptions.map((a) => (
                              <option key={a} value={a}>
                                {a}
                              </option>
                            ))}
                          </Input>
                          <Button size="sm" color="success" onClick={() => saveRow('new')}>
                            <CheckCircleIcon />
                          </Button>
                        </td>
                      </>)}
                    </tr> */}


                    {filteredTimesheets.map((sheet) => {
                      const isEditing = editingRow === sheet.timeId;
                      const rowData = isEditing ? editableData : sheet;

                      return (
                        <tr
                          key={sheet.timeId}
                          className={isEditing ? 'table-active' : ''}
                          style={{
                            backgroundColor: isHoliday(sheet.date) && sheet.hours > 0
                              ? "#fff3cd" // 🟡 Holiday worked
                              : isWeekend(sheet.date)
                                ? "#ec9288"
                                : ""
                          }}
                        >
                          {worksheet && <td style={{ width: '4vw' }}>
                            <div
                              title={sheet.userName} // shows full name on hover
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#007bff', // or use random pastel colors Yaar Azhaippadhu – silent heartbreak vib
                                color: 'white',
                                borderRadius: '50%',
                                width: '36px',
                                height: '36px',
                                fontWeight: 'bold',
                                fontSize: '14px',
                                cursor: 'pointer',
                                textTransform: 'uppercase',
                                boxShadow: '0 0 4px rgba(0,0,0,0.2)',
                              }}
                            >
                              {sheet.userName
                                ? sheet.userName
                                  .split(' ')
                                  .map((n) => n[0])
                                  .join('')
                                  .slice(0, 2)
                                : '?'}
                            </div>
                          </td>}

                          <td
                            style={{
                              width: "5vw",
                              fontSize: "11px",
                              fontWeight: "bold",
                              backgroundColor: getHoursColor(sheet)
                            }}
                          >
                            {isEditing ? (
                              <Input
                                type="date"
                                value={rowData?.date || ""}
                                onChange={(e) => handleCellChange("date", e.target.value)}
                              />
                            ) : (
                              formatDate2(sheet.date)
                            )}
                          </td>
                          <td
                            style={{
                              width: "3vw",
                              fontSize: "11px",
                              fontWeight: "bold",
                              backgroundColor: isHoliday(sheet.date) && sheet.hours > 0
                                ? "#ffc400" // 🟡 Holiday worked
                                : isWeekend(sheet.date)
                                  ? "#ec9288"
                                  : ""
                            }}
                          >
                            {isEditing ? "—" : getDayNamenew(sheet.date)}
                          </td>
                          <td style={{
                            width: '4vw',
                            backgroundColor: getHoursColor(sheet)
                          }}>
                            {isEditing ? (
                              <Input
                                type="select"
                                value={rowData?.hours || ''}
                                onChange={(e) => handleCellChange('hours', Number(e.target.value))}
                              >
                                <option value="">Hours</option>
                                {hourOptions.map((h) => (
                                  <option key={h} value={h}>
                                    {h}
                                  </option>
                                ))}
                              </Input>
                            ) : (
                              sheet.hours
                            )}
                          </td>
                          <td style={{ width: '9vw' }}>{
                            // isEditing ? (
                            //   <Input
                            //     type="select"
                            //     value={rowData?.projectName || ''}
                            //     onChange={(e) => handleCellChange('projectName', e.target.value)}
                            //   >
                            //     <option value="">Select Project</option>
                            //     {projectNames.map((n) => (
                            //       <option key={n} value={n}>
                            //         {n}
                            //       </option>
                            //     ))}
                            //   </Input>
                            // ) : 
                            sheet.projectName}</td>
                          <td style={{ width: '13vw' }}>{
                            // isEditing ? (
                            //   <Input
                            //     type="select"
                            //     value={rowData?.module || ''}
                            //     onChange={(e) => handleCellChange('module', e.target.value)}
                            //   >
                            //     {moduleOptions.map((m) => (
                            //       <option key={m} value={m}>
                            //         {m}
                            //       </option>
                            //     ))}
                            //   </Input>
                            // ) :
                            sheet.module}</td>
                          <td>{sheet.subTask}</td>
                          <td>{sheet.childSubTask}</td>
                          <td style={{ width: "6vw" }}>
                            {isEditing ? (
                              <Input
                                value={rowData?.part || ""}
                                onChange={(e) => handleCellChange("part", e.target.value)}
                              />
                            ) : (
                              <>
                                {sheet.part &&
                                  sheet.part.trim().split(/\s+/).length > 1 ? (
                                  <>
                                    {sheet.part.split(/\s+/).slice(0, 1).join(" ")}...
                                    <Button
                                      size="sm"
                                      color="link"
                                      onClick={() => {
                                        setPartModalText(sheet.part ?? "");
                                        setPartModalOpen(true);
                                      }}
                                    >More..
                                    </Button>

                                  </>
                                ) : (
                                  sheet.part
                                )}
                              </>
                            )}
                          </td>

                          <td style={{ width: '8vw' }}>{isEditing ? (
                            <Input
                              type="select"
                              value={rowData?.product || ''}
                              onChange={(e) => handleCellChange('product', e.target.value)}
                            >
                              {prefixOptions.map((p) => (
                                <option key={p} value={p}>
                                  {p}
                                </option>
                              ))}
                            </Input>
                          ) : sheet.product}</td>
                          <td style={{ width: "5vw" }}>
                            {isEditing ? (
                              rowData?.product?.includes("Drafting") ? (
                                <Input
                                  type="select"
                                  value={rowData?.draftingType ?? ""}
                                  onChange={(e) =>
                                    handleCellChange("draftingType", e.target.value)
                                  }
                                >
                                  <option value="">Select Type</option>
                                  {drawingTypeOptions.map((d) => (
                                    <option key={d.code} value={d.code} title={d.full}>
                                      {d.label}
                                    </option>
                                  ))}
                                </Input>
                              ) : (
                                "—"
                              )
                            ) : (
                              sheet.draftingType || "—"
                            )}
                          </td>
                          <td style={{ width: "6vw" }}>
                            {isEditing ? (
                              rowData?.product?.includes("Drafting") ? (
                                <div style={{ display: "flex", gap: "4px" }}>


                                  {/* Quantity */}
                                  <Input
                                    type="text"
                                    min={0}
                                    style={{ width: "48%" }}
                                    value={rowData?.quantity ?? ""}
                                    onChange={(e) =>
                                      handleCellChange("quantity", Number(e.target.value))
                                    }
                                  />
                                  {/* Total Pages */}
                                  <Input
                                    type="text"
                                    min={0}
                                    style={{ width: "48%" }}
                                    value={rowData?.totPages ?? ""}
                                    onChange={(e) =>
                                      handleCellChange("totPages", Number(e.target.value))
                                    }
                                  />
                                </div>
                              ) : (
                                "—"
                              )
                            ) : (sheet.quantity ?? 0) > 0 ? (
                              `${sheet.quantity ?? 0}/${sheet.totPages ?? 0}`
                            ) : (
                              "—"
                            )}
                          </td>


                          <td >{isEditing ? (
                            <Input value={rowData?.description || ''} onChange={(e) => handleCellChange('description', e.target.value)} />
                          ) : (
                            <>
                              {sheet.description &&
                                sheet.description.trim().split(/\s+/).length > 2 ? (
                                <>
                                  {sheet.description.split(/\s+/).slice(0, 2).join(" ")}...
                                  <Button
                                    size="sm"
                                    color="link"
                                    onClick={() => {
                                      setdescrptionText(sheet.description ?? "");
                                      setdescrptionOpen(true);
                                    }}
                                  >More...
                                  </Button>

                                </>
                              ) : (
                                sheet.description
                              )}
                            </>
                          )}
                          </td>


                          {isAdmin && (
                            <td style={{ width: '6vw' }}>
                              {isEditing ? (
                                <>
                                  <Button size="sm" color="success" onClick={() => saveRow(sheet.timeId)}>
                                    <CheckCircleIcon />
                                  </Button>
                                  <Button size="sm" color="warning" onClick={() => setEditingRow(null)} className="ms-1">
                                    <EditOffIcon />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button size="sm" color="warning" onClick={() => startEdit(sheet)}>
                                    <EditIcon />
                                  </Button>
                                  <Button size="sm" color="danger" onClick={() => handleDelete(sheet.timeId)} className="ms-1">
                                    <DeleteOutlineIcon />
                                  </Button>
                                </>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}


                  </tbody>

                </Table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal isOpen={clientexportModal} toggle={toggleclientExportModal} centered>
        <ModalHeader toggle={toggleclientExportModal}>
          Export TimeSheet
        </ModalHeader>

        <ModalBody>

          <div className="d-flex gap-2 mb-3">

            {/* This Month */}
            <Button
              color="info"
              onClick={() => {
                const now = new Date();
                const year = now.getFullYear();
                const month = now.getMonth() + 1;

                const start = `${year}-${String(month).padStart(2, '0')}-01`;
                const lastDay = new Date(year, month, 0).getDate();
                const end = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;

                setFilters(prev => ({
                  ...prev,
                  startDate: start,
                  endDate: end
                }));
              }}
            >
              This Month
            </Button>

            {/* Last Month */}
            <Button
              color="warning"
              onClick={() => {
                const now = new Date();
                const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);

                const year = lastMonthDate.getFullYear();
                const month = lastMonthDate.getMonth() + 1;

                const start = `${year}-${String(month).padStart(2, '0')}-01`;
                const lastDay = new Date(year, month, 0).getDate();
                const end = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;

                setFilters(prev => ({
                  ...prev,
                  startDate: start,
                  endDate: end
                }));
              }}
            >
              Last Month
            </Button>

          </div>

          {/* Manual Date Selection */}
          <Row>
            <Col md={6}>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) =>
                  setFilters(prev => ({ ...prev, startDate: e.target.value }))
                }
              />
            </Col>

            <Col md={6}>
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) =>
                  setFilters(prev => ({ ...prev, endDate: e.target.value }))
                }
              />
            </Col>
          </Row>

        </ModalBody>

        <ModalFooter>
          <Button
            color="success"
            onClick={() => {
              clienthandleExport();
              toggleclientExportModal();
            }}
          >
            Download
          </Button>

          <Button color="secondary" onClick={toggleclientExportModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>


      <Modal isOpen={exportModal} toggle={toggleExportModal} centered>
        <ModalHeader toggle={toggleExportModal}>
          Export TimeSheet
        </ModalHeader>

        <ModalBody>

          <div className="d-flex gap-2 mb-3">

            {/* This Month */}
            <Button
              color="info"
              onClick={() => {
                const now = new Date();
                const year = now.getFullYear();
                const month = now.getMonth() + 1;

                const start = `${year}-${String(month).padStart(2, '0')}-01`;
                const lastDay = new Date(year, month, 0).getDate();
                const end = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;

                setFilters(prev => ({
                  ...prev,
                  startDate: start,
                  endDate: end
                }));
              }}
            >
              This Month
            </Button>

            {/* Last Month */}
            <Button
              color="warning"
              onClick={() => {
                const now = new Date();
                const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);

                const year = lastMonthDate.getFullYear();
                const month = lastMonthDate.getMonth() + 1;

                const start = `${year}-${String(month).padStart(2, '0')}-01`;
                const lastDay = new Date(year, month, 0).getDate();
                const end = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;

                setFilters(prev => ({
                  ...prev,
                  startDate: start,
                  endDate: end
                }));
              }}
            >
              Last Month
            </Button>

          </div>

          {/* Manual Date Selection */}
          <Row>
            <Col md={6}>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) =>
                  setFilters(prev => ({ ...prev, startDate: e.target.value }))
                }
              />
            </Col>

            <Col md={6}>
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) =>
                  setFilters(prev => ({ ...prev, endDate: e.target.value }))
                }
              />
            </Col>
          </Row>

        </ModalBody>

        <ModalFooter>
          <Button
            color="success"
            onClick={() => {
              handleExport();
              toggleExportModal();
            }}
          >
            Download
          </Button>

          <Button color="secondary" onClick={toggleExportModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>


      {/*  Part No Modal */}
      <Modal isOpen={partModalOpen} toggle={() => setPartModalOpen(false)}>
        <ModalHeader toggle={() => setPartModalOpen(false)}>
          Part No
        </ModalHeader>

        <ModalBody>
          <Input type="textarea" rows={6} value={partModalText} readOnly />
        </ModalBody>
      </Modal>

      {/* Description Modal */}
      <Modal isOpen={descrptionOpen} toggle={() => setdescrptionOpen(false)}>
        <ModalHeader toggle={() => setdescrptionOpen(false)}>
          Description/Remarks
        </ModalHeader>

        <ModalBody>
          <Input type="textarea" rows={6} value={descrptionText} readOnly />
        </ModalBody>
      </Modal>


      {/* Delete Confirmation Modal */}
      <Modal isOpen={modal} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>Confirm Delete</ModalHeader>
        <ModalBody>Are you sure you want to delete this record?</ModalBody>
        <ModalFooter>
          <Button
            color="danger"
            onClick={() => {
              if (selectedTimeId) deleteMutation.mutate(selectedTimeId);
            }}
          >
            Delete
          </Button>
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default CombinedTimesheetComponent;
